import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-slate-500 hover:text-slate-800 mb-8 text-sm"
          data-testid="privacy-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mb-10">Last Updated: March 23, 2026</p>

        <div className="prose prose-slate max-w-none space-y-8 text-[15px] leading-relaxed text-slate-700">
          <p>
            Center Line Locating, LLC ("we," "us," or "our") operates this application and provides utility locating and inspection services. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our internal FieldReady application or our services.
          </p>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Information We Collect</h2>

            <h3 className="text-base font-semibold text-slate-800 mt-6 mb-2">Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide when you:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Log into your FieldReady account</li>
              <li>Submit service request forms</li>
              <li>Upload project files and site plans</li>
              <li>Record time entries and field reports</li>
              <li>Communicate with us via email or phone</li>
            </ul>

            <p className="mt-4">This information may include:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Name and contact information</li>
              <li>Email address and phone number</li>
              <li>Company name and title</li>
              <li>Project location and site details</li>
              <li>Time tracking and GPS location data</li>
              <li>Billing and accounts payable information</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-800 mt-6 mb-2">Uploaded Files</h3>
            <p>When using FieldReady, you may upload files including site plans, PDFs, images, KMZ files, and other project documents. These files are:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Stored securely on our cloud servers (Cloudinary)</li>
              <li>Used solely to fulfill service and project requirements</li>
              <li>Retained for 7 years for project records and legal compliance</li>
              <li>Never sold or shared with third parties without your consent</li>
            </ul>

            <h3 className="text-base font-semibold text-slate-800 mt-6 mb-2">Automatically Collected Information</h3>
            <p>When you use this application, we may automatically collect:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>IP address</li>
              <li>Browser type and device information</li>
              <li>GPS location data (when using Time Tracker features)</li>
              <li>Application usage patterns</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Manage job scheduling, assignments, and tracking</li>
              <li>Provide utility locating, inspection, and related services</li>
              <li>Send project updates and service communications</li>
              <li>Process billing, timesheets, and expense reports</li>
              <li>Improve our application and services</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Third-Party Services</h2>
            <p>We use the following services to operate our application and business:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li><strong>File Storage:</strong> Cloudinary (for document and image storage)</li>
              <li><strong>Hosting:</strong> Cloud-based hosting services (application and database)</li>
              <li><strong>Email Delivery:</strong> Resend (for sending notifications)</li>
            </ul>
            <p className="mt-2">These services may collect data as described in their respective privacy policies.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Information Sharing</h2>
            <p>We do not sell, trade, or rent your personal information to third parties. We may share your information with:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Service providers who assist in our operations (under confidentiality agreements)</li>
              <li>Professional advisors (lawyers, accountants) as needed</li>
              <li>Government authorities when required by law</li>
              <li>Project stakeholders only with your explicit consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Data Retention</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Employee records and time entries:</strong> Retained for duration of employment + 3 years</li>
              <li><strong>Service request records:</strong> Retained for 7 years (business and legal records)</li>
              <li><strong>Uploaded project files:</strong> Retained for 7 years</li>
              <li><strong>Application backups:</strong> Retained for 30 days (automatic daily backups)</li>
            </ul>
            <p className="mt-2">You may request deletion of your data at any time, subject to our legal retention requirements.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Data Security</h2>
            <p>We implement appropriate technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction, including:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Secure encrypted connections (HTTPS)</li>
              <li>Password-protected user accounts</li>
              <li>Secure file storage with backup redundancy</li>
              <li>Access controls limiting who can view your information</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Your Rights</h2>
            <p>You have the right to:</p>
            <ul className="list-disc pl-6 space-y-1 mt-2">
              <li>Access your personal information we hold</li>
              <li>Correct inaccurate information</li>
              <li>Request deletion of your information (subject to legal retention requirements)</li>
              <li>Opt-out of marketing communications</li>
              <li>Request a copy of your data</li>
            </ul>
            <p className="mt-2">To exercise these rights, contact us using the information below.</p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Contact Us</h2>
            <p>If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us at:</p>
            <div className="mt-3 bg-slate-50 rounded-lg p-4 text-sm">
              <p className="font-semibold text-slate-900">Center Line Locating, LLC</p>
              <p>12030 Goodrich Drive, Charlotte, NC 28273</p>
              <p className="mt-2">Email: <a href="mailto:requests@cllocating.com" className="text-blue-600 hover:underline">requests@cllocating.com</a></p>
              <p>Phone: <a href="tel:+17043155820" className="text-blue-600 hover:underline">(704) 315-5820</a></p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy within the application and updating the "Last Updated" date.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
